// Guided Tour Context — Manages product tour state across the app
// Persists progress to localStorage + optional backend sync
// Provides tour steps, navigation, and completion tracking

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

// ── Tour Step Definition ──

export interface TourStep {
  id: string;
  title: string;
  description: string;
  target?: string;           // CSS selector for element to highlight
  page?: string;             // Route path where this step applies
  action?: string;           // Action hint: 'click', 'navigate', 'observe'
  actionLabel?: string;      // Button label for the action
  actionRoute?: string;      // Route to navigate to
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  category: 'welcome' | 'navigation' | 'data' | 'intelligence' | 'execution' | 'governance';
}

// ── Tour Definitions ──

export const WELCOME_TOUR: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to VAERION',
    description: 'VAERION is your governed AI execution operating system. It understands your customers, decides what to do, acts under policy control, and proves what created value. Let\'s walk through how it all works.',
    position: 'center',
    category: 'welcome',
  },
  {
    id: 'nav-command-center',
    title: 'Command Center',
    description: 'Your operational dashboard. See AI-generated briefings, pending actions, alerts, and system health at a glance. Everything starts here.',
    target: '[data-tour="nav-command"]',
    action: 'navigate',
    actionLabel: 'Go to Command Center',
    actionRoute: '/',
    position: 'right',
    category: 'navigation',
  },
  {
    id: 'nav-customers',
    title: 'Customers',
    description: 'Your unified customer memory. Contacts, companies, pipeline deals, and conversations — all connected through identity resolution into living operational entities.',
    target: '[data-tour="nav-customers"]',
    action: 'navigate',
    actionLabel: 'View Customers',
    actionRoute: '/crm/contacts',
    position: 'right',
    category: 'navigation',
  },
  {
    id: 'nav-intelligence',
    title: 'Intelligence Hub',
    description: 'The brain of the system. Six-dimension customer intelligence, behavioral scoring, digital twin simulations, and AI-powered strategic insights — all computed from real data.',
    target: '[data-tour="nav-intelligence"]',
    action: 'navigate',
    actionLabel: 'Explore Intelligence',
    actionRoute: '/intelligence',
    position: 'right',
    category: 'intelligence',
  },
  {
    id: 'nav-execution',
    title: 'Execution Engine',
    description: '20 AI agents, workflow synthesis, and multi-channel execution. The system generates personalized workflows from customer intelligence and executes them under bounded autonomy.',
    target: '[data-tour="nav-execution"]',
    action: 'navigate',
    actionLabel: 'View Execution',
    actionRoute: '/execution',
    position: 'right',
    category: 'execution',
  },
  {
    id: 'nav-governance',
    title: 'Governance & Audit',
    description: 'Every action traced. SHA-256 Merkle-chained event ledger, decision receipts, policy versioning, and causal lift measurement. Full compliance auditability.',
    target: '[data-tour="nav-governance"]',
    action: 'navigate',
    actionLabel: 'View Governance',
    actionRoute: '/governance',
    position: 'right',
    category: 'governance',
  },
  {
    id: 'the-loop',
    title: 'The ONE System Loop',
    description: 'Everything connects: Data flows in → Intelligence scores customers → Workflows synthesize → Agents execute → Decisions are traced → Lift is measured → Intelligence improves. One coherent operating loop, not a feature pile.',
    position: 'center',
    category: 'welcome',
  },
  {
    id: 'getting-started',
    title: 'Let\'s Get Started',
    description: 'Your Getting Started dashboard shows your setup progress. Complete each milestone to activate the full operating loop: import contacts, connect channels, run your first execution, and watch the intelligence build.',
    action: 'navigate',
    actionLabel: 'Go to Getting Started',
    actionRoute: '/',
    position: 'center',
    category: 'welcome',
  },
];

export const FIRST_CONTACT_GUIDE: TourStep[] = [
  {
    id: 'create-contact-intro',
    title: 'Create Your First Contact',
    description: 'Contacts are the foundation of the intelligence engine. Once created, the system starts building behavioral profiles, scoring churn risk, and computing engagement intelligence.',
    position: 'center',
    category: 'data',
  },
  {
    id: 'create-contact-action',
    title: 'Add a Contact',
    description: 'Click "New Contact" to add your first customer. Fill in their name, email, and phone number. The system will auto-create consent records and begin intelligence computation.',
    action: 'navigate',
    actionLabel: 'Go to Contacts',
    actionRoute: '/crm/contacts',
    position: 'center',
    category: 'data',
  },
];

export const FIRST_EMAIL_GUIDE: TourStep[] = [
  {
    id: 'send-email-intro',
    title: 'Send Your First Email',
    description: 'Communication events feed the intelligence engine. Every email sent is logged, tracked, and used to compute response likelihood and engagement patterns.',
    position: 'center',
    category: 'execution',
  },
  {
    id: 'send-email-action',
    title: 'Compose an Email',
    description: 'Navigate to Conversations and compose your first email. Make sure you\'ve verified a sender domain in Settings > Communication first.',
    action: 'navigate',
    actionLabel: 'Go to Conversations',
    actionRoute: '/conversations',
    position: 'center',
    category: 'execution',
  },
];

// ── All available guided flows ──

export const GUIDED_FLOWS: Record<string, { title: string; steps: TourStep[]; prerequisite?: string }> = {
  welcome: { title: 'Welcome Tour', steps: WELCOME_TOUR },
  first_contact: { title: 'Create Your First Contact', steps: FIRST_CONTACT_GUIDE, prerequisite: 'welcome' },
  first_email: { title: 'Send Your First Email', steps: FIRST_EMAIL_GUIDE, prerequisite: 'first_contact' },
};

// ── Tour State ──

interface TourState {
  isActive: boolean;
  currentFlow: string | null;
  currentStepIndex: number;
  completedFlows: string[];
  dismissedAt: string | null;
}

interface TourContextValue {
  state: TourState;
  currentStep: TourStep | null;
  totalSteps: number;
  startTour: (flowId: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  isFlowCompleted: (flowId: string) => boolean;
  shouldShowTour: boolean;
}

const STORAGE_KEY = 'vaerion_tour_state';

const DEFAULT_TOUR_STATE: TourState = { isActive: false, currentFlow: null, currentStepIndex: 0, completedFlows: [], dismissedAt: null };

function loadTourState(): TourState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...DEFAULT_TOUR_STATE, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return { ...DEFAULT_TOUR_STATE };
}

function saveTourState(state: TourState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TourState>(loadTourState);

  useEffect(() => {
    saveTourState(state);
  }, [state]);

  const currentFlow = state.currentFlow ? GUIDED_FLOWS[state.currentFlow] : null;
  const currentStep = currentFlow?.steps[state.currentStepIndex] ?? null;
  const totalSteps = currentFlow?.steps.length ?? 0;

  const startTour = useCallback((flowId: string) => {
    setState(s => ({ ...s, isActive: true, currentFlow: flowId, currentStepIndex: 0, dismissedAt: null }));
  }, []);

  const nextStep = useCallback(() => {
    setState(s => {
      const flow = s.currentFlow ? GUIDED_FLOWS[s.currentFlow] : null;
      if (!flow) return s;
      if (s.currentStepIndex >= flow.steps.length - 1) {
        // Tour complete
        return {
          ...s,
          isActive: false,
          currentFlow: null,
          currentStepIndex: 0,
          completedFlows: [...new Set([...s.completedFlows, s.currentFlow!])],
        };
      }
      return { ...s, currentStepIndex: s.currentStepIndex + 1 };
    });
  }, []);

  const prevStep = useCallback(() => {
    setState(s => ({
      ...s,
      currentStepIndex: Math.max(0, s.currentStepIndex - 1),
    }));
  }, []);

  const skipTour = useCallback(() => {
    setState(s => ({
      ...s,
      isActive: false,
      currentFlow: null,
      currentStepIndex: 0,
      dismissedAt: new Date().toISOString(),
    }));
  }, []);

  const completeTour = useCallback(() => {
    setState(s => ({
      ...s,
      isActive: false,
      currentFlow: null,
      currentStepIndex: 0,
      completedFlows: s.currentFlow
        ? [...new Set([...s.completedFlows, s.currentFlow])]
        : s.completedFlows,
    }));
  }, []);

  const isFlowCompleted = useCallback((flowId: string) => {
    return state.completedFlows.includes(flowId);
  }, [state.completedFlows]);

  // Show tour auto-prompt if welcome not completed and not dismissed
  const shouldShowTour = !state.completedFlows.includes('welcome') && !state.dismissedAt && !state.isActive;

  return (
    <TourContext.Provider value={{
      state, currentStep, totalSteps,
      startTour, nextStep, prevStep, skipTour, completeTour,
      isFlowCompleted, shouldShowTour,
    }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
}
